import { useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Save as Waves, Fish, Anchor, Shell, Compass } from "lucide-react";
import { Container } from "@/components/shared/Primitives";
import { Reveal } from "@/components/shared/Reveal";
import { OceanScene } from "@/components/shared/OceanScene";
import { LAB_EXPERIMENTS, LAB_CHANGELOG } from "@/data/site";
import { useTheme } from "@/context/ThemeProvider";

const ACCENT = "#1fd0e6";
const EASE = [0.22, 1, 0.36, 1];

const TEXT = "#eafdff";
const TEXT_SOFT = "rgba(223,249,255,0.82)";
const TEXT_DIM = "rgba(200,240,252,0.6)";
const SHADOW = "0 2px 18px rgba(2,18,40,0.55)";

const STATUS_TINT = {
  Live:      { c: "#7dffb8", bg: "rgba(60,220,150,0.16)",  bd: "rgba(125,255,184,0.5)" },
  Beta:      { c: "#ffd773", bg: "rgba(255,200,80,0.16)",  bd: "rgba(255,215,115,0.5)" },
  Prototype: { c: "#ff9ec0", bg: "rgba(255,126,157,0.16)", bd: "rgba(255,158,192,0.5)" },
};

const FILTER_ICON = { All: Compass, AI: Shell, Data: Waves, Design: Fish, Tools: Anchor };

// ─── Hero dive-in keyframes ───────────────────────────────────────────────────
const DIVE_CSS = `
  @keyframes hv-wave-fwd  { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  @keyframes hv-wave-rev  { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
  @keyframes hv-shimmer   { 0%, 100% { opacity: .07; } 50% { opacity: .22; } }
  @keyframes hv-star      { 0%, 100% { opacity: var(--so,.32); transform: scale(1); } 50% { opacity: var(--so2,.82); transform: scale(1.4); } }
  @keyframes hv-sun-pulse { 0%, 100% { box-shadow: 0 0 40px rgba(255,206,50,.80), 0 0 80px rgba(255,180,0,.40); }
                             50%       { box-shadow: 0 0 58px rgba(255,206,50,.95), 0 0 110px rgba(255,180,0,.54); } }
  @keyframes hv-moon-glow { 0%, 100% { box-shadow: 0 0 28px rgba(180,214,255,.48), 0 0 56px rgba(180,214,255,.18); }
                             50%       { box-shadow: 0 0 42px rgba(180,214,255,.68), 0 0 78px rgba(180,214,255,.28); } }
  .hv-wave-fwd  { animation: hv-wave-fwd 18s linear infinite; }
  .hv-wave-rev  { animation: hv-wave-rev 26s linear infinite; }
  .hv-shimmer   { animation: hv-shimmer var(--sd,6s) ease-in-out var(--dl,0s) infinite; }
  .hv-star      { animation: hv-star var(--sd,3s) ease-in-out var(--dl,0s) infinite; }
  .hv-sun-pulse { animation: hv-sun-pulse 5s ease-in-out infinite; }
  .hv-moon-glow { animation: hv-moon-glow 6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .hv-wave-fwd, .hv-wave-rev, .hv-shimmer, .hv-star, .hv-sun-pulse, .hv-moon-glow { animation: none !important; }
  }
`;

const SKY_H = "52vh"; // horizon line

// Single wave SVG tile (two placed side-by-side for seamless loop)
const WaveTile = ({ d, fill }) => (
  <svg viewBox="0 0 1440 60" preserveAspectRatio="none"
    style={{ width: "50%", display: "inline-block", verticalAlign: "top" }}>
    <path d={d} fill={fill} />
  </svg>
);

// ─── Sky portion of the hero ─────────────────────────────────────────────────
function HeroSky({ light }) {
  const reduce = useReducedMotion();

  const stars = useMemo(
    () =>
      !light
        ? Array.from({ length: 38 }, (_, i) => ({
            x: ((i * 137.5 + i * 3.14) % 94).toFixed(1),
            y: ((i * 61.8 + i * 2.71) % 84).toFixed(1),
            size: 0.9 + (i % 4) * 0.5,
            op:   0.26 + (i % 5) * 0.10,
            dur:  (2.1 + (i % 8) * 0.42).toFixed(1),
            delay: ((i * 0.44) % 5.2).toFixed(1),
          }))
        : [],
    [light]
  );

  return (
    <div aria-hidden="true" className="absolute inset-x-0 top-0 overflow-hidden"
      style={{ height: SKY_H }}>

      {/* Gradient */}
      <div className="absolute inset-0" style={{
        background: light
          ? "linear-gradient(180deg, #3a9ee2 0%, #5cb8f0 22%, #88d2f8 50%, #aee5ff 74%, rgba(148,226,244,.70) 100%)"
          : "linear-gradient(180deg, #010610 0%, #030c1e 28%, #050f2a 58%, rgba(6,18,42,.78) 100%)"
      }} />

      {/* Stars (dark mode) */}
      {!light && stars.map((s, i) => (
        <span
          key={i}
          className={reduce ? "absolute rounded-full" : "hv-star absolute rounded-full"}
          style={{
            left: `${s.x}%`, top: `${s.y}%`,
            width: s.size, height: s.size,
            opacity: reduce ? s.op : undefined,
            "--so": s.op, "--so2": Math.min(1, s.op + 0.52),
            "--sd": `${s.dur}s`, "--dl": `${s.delay}s`,
            background: "#d6ecff",
            boxShadow: s.size > 1.5 ? "0 0 6px rgba(200,228,255,.75)" : "none",
          }}
        />
      ))}

      {/* Sun (light mode) */}
      {light && (
        <div className="absolute" style={{ right: "9%", top: "18%" }}>
          <div style={{ position: "absolute", inset: -52, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,218,80,.40) 0%, rgba(255,186,20,.10) 48%, transparent 72%)",
            filter: "blur(10px)" }} />
          <div className="hv-sun-pulse" style={{ width: 82, height: 82, borderRadius: "50%",
            background: "radial-gradient(circle at 40% 36%, #fff8d0 0%, #ffe766 28%, #ffce1a 58%, #ffb300 100%)" }} />
        </div>
      )}

      {/* Moon (dark mode) */}
      {!light && (
        <div className="absolute" style={{ right: "10%", top: "14%" }}>
          <div style={{ position: "absolute", inset: -38, borderRadius: "50%",
            background: "radial-gradient(circle, rgba(180,214,255,.28) 0%, transparent 72%)",
            filter: "blur(10px)" }} />
          <div className="hv-moon-glow" style={{ width: 84, height: 84, borderRadius: "50%",
            background: "radial-gradient(circle at 36% 32%, #f2f8ff 0%, #d8ecff 32%, #c0daf8 62%, #a4c6ee 100%)",
            position: "relative" }}>
            <div style={{ position: "absolute", width: 17, height: 17, borderRadius: "50%", top: "27%", left: "22%",
              background: "radial-gradient(circle, rgba(148,182,228,.42) 0%, transparent 70%)" }} />
            <div style={{ position: "absolute", width: 11, height: 11, borderRadius: "50%", top: "54%", left: "56%",
              background: "radial-gradient(circle, rgba(148,182,228,.36) 0%, transparent 70%)" }} />
          </div>
        </div>
      )}

      {/* Horizon haze — blends sky into the wave below */}
      <div className="absolute inset-x-0 bottom-0" style={{ height: 80,
        background: light
          ? "linear-gradient(180deg, transparent 0%, rgba(130,215,238,.62) 100%)"
          : "linear-gradient(180deg, transparent 0%, rgba(8,34,62,.72) 100%)" }} />
    </div>
  );
}

// ─── Animated wave surface ───────────────────────────────────────────────────
function HeroWaves({ light }) {
  const reduce = useReducedMotion();

  const fillA = light ? "rgba(120,228,248,.84)" : "rgba(12,56,90,.90)";
  const fillB = light ? "rgba(78,196,230,.58)"  : "rgba(7,40,70,.68)";
  const dA = "M0,32 C180,66 340,4 560,30 C780,58 940,6 1160,34 C1300,54 1400,26 1440,32 L1440,60 L0,60 Z";
  const dB = "M0,46 C220,16 380,74 620,44 C840,18 1000,70 1240,42 C1360,30 1420,48 1440,42 L1440,60 L0,60 Z";

  return (
    <div aria-hidden="true" className="absolute inset-x-0" style={{ top: `calc(${SKY_H} - 30px)`, zIndex: 6 }}>
      <div style={{ overflow: "hidden", lineHeight: 0 }}>
        <div style={{ width: "200%" }} className={reduce ? undefined : "hv-wave-fwd"}>
          <WaveTile d={dA} fill={fillA} /><WaveTile d={dA} fill={fillA} />
        </div>
      </div>
      <div style={{ overflow: "hidden", lineHeight: 0, marginTop: -22 }}>
        <div style={{ width: "200%" }} className={reduce ? undefined : "hv-wave-rev"}>
          <WaveTile d={dB} fill={fillB} /><WaveTile d={dB} fill={fillB} />
        </div>
      </div>
    </div>
  );
}

// ─── Light refraction shimmer just below the waterline ───────────────────────
function WaterShimmer() {
  const reduce = useReducedMotion();
  const rays = [
    { left: "6%",  w: 58, dur: 7,  dl: 0, skew:  14 },
    { left: "20%", w: 40, dur: 9,  dl: 2, skew: -12 },
    { left: "44%", w: 72, dur: 6,  dl: 4, skew:  11 },
    { left: "66%", w: 46, dur: 8,  dl: 1, skew: -14 },
    { left: "84%", w: 54, dur: 10, dl: 3, skew:  12 },
  ];
  return (
    <div aria-hidden="true" className="absolute inset-x-0 overflow-hidden"
      style={{ top: SKY_H, height: "30vh", zIndex: 4 }}>
      {rays.map((r, i) => (
        <div
          key={i}
          className={reduce ? "absolute" : "hv-shimmer absolute"}
          style={{
            left: r.left, top: 0, width: r.w, height: "90%",
            background: "linear-gradient(180deg, rgba(200,246,255,.55) 0%, transparent 100%)",
            transform: `skewX(${r.skew}deg)`, filter: "blur(12px)",
            opacity: reduce ? 0.08 : undefined,
            "--sd": `${r.dur}s`, "--dl": `${r.dl}s`,
          }}
        />
      ))}
    </div>
  );
}

// ─── Experiment card ─────────────────────────────────────────────────────────
function BubbleCard({ exp, index }) {
  const reduce = useReducedMotion();
  const Icon = exp.icon;
  const st = STATUS_TINT[exp.status] ?? STATUS_TINT.Prototype;

  return (
    <motion.div
      animate={reduce ? undefined : { y: [0, -9, 0] }}
      transition={reduce ? undefined : { duration: 5 + (index % 4), repeat: Infinity, ease: "easeInOut", delay: (index % 6) * 0.4 }}
      style={{ willChange: "transform" }}
    >
      <div
        className="group relative flex h-full flex-col overflow-hidden rounded-[20px] p-6 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 hover:scale-[1.025]"
        style={{
          background: "linear-gradient(160deg, rgba(255,255,255,0.12), rgba(120,220,240,0.05))",
          backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)",
          border: "1px solid rgba(150,238,255,0.32)",
          boxShadow: "0 10px 36px rgba(2,22,48,0.4), inset 0 1px 0 rgba(255,255,255,0.22)",
        }}
      >
        <span className="pointer-events-none absolute inset-0 rounded-[20px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
          style={{ boxShadow: `0 0 0 1px ${ACCENT}, 0 0 38px rgba(31,208,230,0.55)` }} />
        <span className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full opacity-50"
          style={{ background: "radial-gradient(circle at 35% 35%, rgba(255,255,255,0.5), transparent 60%)" }} />

        <span className="font-tech absolute right-4 top-4 rounded-full px-2.5 py-1 text-[9px] uppercase tracking-[0.12em]"
          style={{ background: "rgba(8,40,70,0.5)", color: "#bff4ff", border: "1px solid rgba(150,238,255,0.32)" }}>
          {exp.tag}
        </span>

        <span className="mb-5 grid h-12 w-12 place-items-center rounded-[14px] transition-transform duration-300 group-hover:scale-110"
          style={{
            background: "radial-gradient(circle at 35% 30%, rgba(125,255,235,0.32), rgba(31,208,230,0.12))",
            color: "#cffcff", border: "1px solid rgba(150,238,255,0.4)",
            boxShadow: "0 0 22px rgba(31,208,230,0.45)",
          }}>
          <Icon className="h-5 w-5" />
        </span>

        <h3 className="text-lg font-semibold" style={{ color: TEXT, textShadow: SHADOW }}>{exp.title}</h3>
        <p className="mt-2.5 flex-1 text-[14px] font-light leading-relaxed" style={{ color: TEXT_SOFT }}>{exp.blurb}</p>

        <div className="mt-6 flex items-center justify-between">
          <span className="font-tech inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.12em]"
            style={{ background: st.bg, color: st.c, border: `1px solid ${st.bd}` }}>
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: st.c }} />
            {exp.status}
          </span>
          <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: "#bff4ff" }} />
        </div>
      </div>
    </motion.div>
  );
}

function PitchCTA() {
  const reduce = useReducedMotion();
  return (
    <div className="flex flex-col items-center gap-3">
      <motion.div
        animate={reduce ? undefined : { y: [0, -8, 0] }}
        transition={reduce ? undefined : { duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        style={{ willChange: "transform" }}
      >
        <Link
          to="/contact"
          data-testid="circle-cta"
          className="group relative grid h-[148px] w-[148px] place-items-center rounded-full text-center transition-transform duration-300 hover:scale-[1.05]"
          style={{
            background: "radial-gradient(circle at 38% 32%, rgba(125,255,235,0.22), rgba(31,208,230,0.08))",
            border: "1px solid rgba(150,238,255,0.6)",
            boxShadow: "0 0 50px rgba(31,208,230,0.4), inset 0 0 40px rgba(125,255,235,0.12)",
            backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
          }}
        >
          <span className="font-display text-[19px] leading-[1.1] tracking-[0.08em]"
            style={{ color: "#aef6ff", textShadow: "0 0 24px rgba(105,240,255,0.7)" }}>
            PITCH<br />IT
          </span>
          <ArrowUpRight className="mt-1.5 h-5 w-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            style={{ color: "#aef6ff" }} />
        </Link>
      </motion.div>
      <span className="text-center text-[11px] tracking-[0.04em]" style={{ color: TEXT_DIM }}>We read everything</span>
    </div>
  );
}

export default function Lab() {
  const { theme } = useTheme();
  const light = theme !== "dark";

  const tags = useMemo(() => ["All", ...Array.from(new Set(LAB_EXPERIMENTS.map((e) => e.tag)))], []);
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? LAB_EXPERIMENTS : LAB_EXPERIMENTS.filter((e) => e.tag === active);
  const countFor = (t) => (t === "All" ? LAB_EXPERIMENTS.length : LAB_EXPERIMENTS.filter((e) => e.tag === t).length);

  return (
    <div className="relative overflow-hidden" style={{ "--page-accent": ACCENT }}>
      <style>{DIVE_CSS}</style>
      <OceanScene />

      <div className="relative z-10">
        {/* HERO — dive-in effect */}
        <section className="relative overflow-hidden" style={{ minHeight: "88vh" }}>
          {/* Sky backdrop */}
          <HeroSky light={light} />

          {/* Animated wave surface at the horizon */}
          <HeroWaves light={light} />

          {/* Light refraction shimmer just below the waterline */}
          <WaterShimmer />

          {/* Content — sky zone holds eyebrow + title, water zone holds description */}
          <div className="relative z-20 flex flex-col" style={{ minHeight: "88vh" }}>
            {/* ── Sky zone: content pinned to its bottom edge (the waterline) ── */}
            <div className="flex flex-col justify-end px-5 sm:px-8 lg:px-16"
              style={{ height: SKY_H, paddingTop: "4.5rem", paddingBottom: "1.25rem", flexShrink: 0 }}>
              <Reveal>
                <div className="inline-flex items-center gap-2.5">
                  <Waves className="h-4 w-4" style={{ color: "#9bf6ff" }} />
                  <span className="font-tech text-[11px] uppercase tracking-[0.2em]"
                    style={{ color: "#9bf6ff", textShadow: SHADOW }}>
                    Experiments · Prototypes · R&amp;D
                  </span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  className="font-display mt-6"
                  style={{
                    fontSize: "clamp(58px, 11vw, 168px)",
                    lineHeight: 0.86,
                    letterSpacing: "0.02em",
                    color: TEXT,
                    textShadow: "0 6px 40px rgba(2,18,40,0.6)",
                  }}
                >
                  THE{" "}
                  <span style={{ color: "#69f0ff", textShadow: "0 0 36px rgba(105,240,255,0.75)" }}>LAB</span>
                </h1>
              </Reveal>
            </div>

            {/* ── Water zone: description sits below the waterline ── */}
            <div className="flex flex-col justify-start px-5 pb-14 sm:px-8 lg:px-16"
              style={{ paddingTop: "4rem", flex: 1 }}>
              <Reveal delay={0.12}>
                <p className="max-w-xl text-[15px] font-light leading-relaxed" style={{ color: TEXT_SOFT }}>
                  Half-built ideas, on purpose. Dive in: this is where we ship prototypes, micro-tools and weird
                  experiments to learn in public. Some surface into the apps. Some are just for fun.
                </p>
              </Reveal>
            </div>
          </div>
        </section>

        {/* FILTER + GRID */}
        <section className="py-10 lg:py-14">
          <Container>
            <div data-testid="lab-filter-group" className="mb-10 flex flex-wrap items-center gap-2.5">
              {tags.map((t) => {
                const on = active === t;
                const ChipIcon = FILTER_ICON[t] ?? Shell;
                return (
                  <button
                    key={t}
                    data-testid="lab-filter-chip"
                    onClick={() => setActive(t)}
                    className="font-tech inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.1em] transition-[background-color,color,border-color,box-shadow] duration-200"
                    style={on
                      ? { background: "rgba(31,208,230,0.2)", color: "#d4fbff", border: "1px solid rgba(150,238,255,0.7)", boxShadow: "0 0 22px rgba(31,208,230,0.5)" }
                      : { background: "rgba(255,255,255,0.06)", color: TEXT_SOFT, border: "1px solid rgba(150,238,255,0.22)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
                  >
                    <ChipIcon className="h-3.5 w-3.5" style={{ color: on ? "#9bf6ff" : TEXT_DIM }} />
                    {t}
                    <span style={{ color: on ? "#9bf6ff" : TEXT_DIM }}>{countFor(t)}</span>
                  </button>
                );
              })}
            </div>

            <motion.div
              key={active}
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.06 } } }}
              className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            >
              {filtered.map((e, i) => (
                <motion.div
                  key={e.title}
                  variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } } }}
                >
                  <BubbleCard exp={e} index={i} />
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </section>

        {/* CHANGELOG */}
        <section className="py-20 lg:py-24">
          <Container>
            <Reveal>
              <div className="mb-10 flex items-center gap-4 sm:mb-14">
                <span className="font-display whitespace-nowrap text-[13px] tracking-[0.18em]" style={{ color: TEXT_SOFT }}>
                  SIGNAL / 02 — 03
                </span>
                <div className="h-px flex-1" style={{ background: "linear-gradient(90deg, rgba(150,238,255,0.6) 0%, transparent 100%)" }} />
                <span className="font-tech hidden items-center gap-2 text-[11px] uppercase tracking-[0.18em] sm:inline-flex" style={{ color: TEXT_DIM }}>
                  <Anchor className="h-3.5 w-3.5" /> Field notes
                </span>
              </div>
            </Reveal>
            <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr]">
              <Reveal>
                <h2 className="text-2xl font-semibold leading-tight sm:text-3xl" style={{ color: TEXT, textShadow: SHADOW }}>
                  A running log of what surfaced and what we learned.
                </h2>
              </Reveal>
              <div>
                {LAB_CHANGELOG.map((c, i) => (
                  <Reveal key={c.date} delay={i * 0.05}>
                    <div className="flex gap-6 border-b py-5" style={{ borderColor: "rgba(150,238,255,0.18)" }}>
                      <span className="font-tech w-20 flex-shrink-0 text-[12px]" style={{ color: "#9bf6ff" }}>{c.date}</span>
                      <p className="text-[15px] font-light leading-relaxed" style={{ color: TEXT_SOFT }}>{c.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* CTA */}
        <section className="relative py-24">
          <Container className="grid items-end gap-12 lg:grid-cols-[1fr_auto]">
            <div>
              <div className="inline-flex items-center gap-2.5">
                <Fish className="h-4 w-4" style={{ color: "#9bf6ff" }} />
                <span className="font-tech text-[11px] uppercase tracking-[0.2em]" style={{ color: "#9bf6ff", textShadow: SHADOW }}>
                  Got a wild idea?
                </span>
              </div>
              <h2
                className="font-display mt-6"
                style={{ fontSize: "clamp(40px, 5.6vw, 84px)", lineHeight: 0.96, letterSpacing: "0.02em", color: TEXT, textShadow: "0 6px 40px rgba(2,18,40,0.6)" }}
              >
                PITCH US SOMETHING<br />WORTH BUILDING.
              </h2>
            </div>
            <PitchCTA />
          </Container>
        </section>
      </div>
    </div>
  );
}
